import { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import useProofsOfOwnership from "~hooks/useProofsOfOwnership"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import PATHS, { getFullPath } from "~constants/paths"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"
import UploadProofOfOwnershipModal from "~components/proofOfOwnership/UploadProofOfOwnershipModal"
import { UserShape } from "~shapes/user"
import moment from "moment"
import { PooInitialState } from "../../../initialStates"
import { useMutation } from "react-query"
import ProofOfOwnershipViewer from "~components/proofOfOwnership/ProofOfOwnershipViewer"
import ProofOfOwnershipListItem from "~components/proofOfOwnership/ProofOfOwnershipListItem"
import ProofOfOwnershipService from "~services/ProofOfOwnershipService"
import colors from "~constants/colors"
import { rgba } from "polished"
import { toast } from "react-toastify"

interface Props {
  user: UserShape
}

const stateLabels: Record<string, { label: string; color: string }> = {
  [PROOF_OF_OWNERSHIP_STATUS.CREATED]: {
    label: "Created",
    color: colors.blue2,
  },
  [PROOF_OF_OWNERSHIP_STATUS.VALIDATING]: {
    label: "Validating",
    color: "#F5A623",
  },
  [PROOF_OF_OWNERSHIP_STATUS.VALIDATED]: {
    label: "Validated",
    color: colors.green,
  },
  [PROOF_OF_OWNERSHIP_STATUS.INVALIDATED]: {
    label: "Invalidated",
    color: colors.red,
  },
  [PROOF_OF_OWNERSHIP_STATUS.DISCARDED]: {
    label: "Discarded",
    color: colors.gray4,
  },
  [PROOF_OF_OWNERSHIP_STATUS.UPDATING]: {
    label: "Updating",
    color: colors.sky,
  },
  [PROOF_OF_OWNERSHIP_STATUS.UNDETERMINED]: {
    label: "Undetermined",
    color: colors.gray6,
  },
}

const ProofOfOwnershipHistorical = ({ user }: Props) => {
  const [selectedPoo, setSelectedPoo] =
    useState<ProofOfOwnership>(PooInitialState)
  const [isPooUploadModalOpened, setIsPooUploadModalOpened] =
    useState<boolean>(false)
  const [isEditingName, setIsEditingName] = useState<boolean>(false)
  const [editNameValue, setEditNameValue] = useState<string>("")

  const {
    data: pooList,
    refetch: fetchPooList,
    isError: isErrorFetchingPooList,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    // Enabled so React Query owns the fetch (it dedupes the StrictMode
    // double-mount into a single request and reliably commits the result).
    // Newest-first so a freshly uploaded POO lands on the first page; the
    // backend orders by created_at via the userAndCreatedAtIndex GSI, so no
    // client-side re-sort is needed.
  } = useProofsOfOwnership(user.id, undefined, true, "created_at,desc")

  const { mutateAsync: patchName, isLoading: isSavingName } = useMutation(
    (name: string) =>
      ProofOfOwnershipService.patchProofOfOwnershipName(selectedPoo.id, name),
    {
      onSuccess: (_data, name) => {
        setSelectedPoo(prev => ({ ...prev, name }))
        setIsEditingName(false)
        toast.success("Name updated successfully")
        fetchPooList()
      },
      onError: () => {
        toast.error("Failed to update name")
      },
    },
  )

  // The backend already returns POOs newest-first, so render them as-is — no
  // client-side re-sort needed.
  const pooItems = useMemo(() => pooList ?? [], [pooList])

  // Once the list loads (or changes), select the first (newest) POO if nothing
  // is selected yet.
  useEffect(() => {
    if (pooItems.length > 0 && selectedPoo.id === PooInitialState.id) {
      setSelectedPoo(pooItems[0])
    }
  }, [pooItems])

  const handleRefresh = async () => {
    try {
      const result = await fetchPooList()
      // After an upload, select the just-uploaded POO. The backend returns
      // newest-first, so it's the first item. The infinite query resolves to
      // paged data, so flatten the pages first.
      const items = result.data?.pages?.flatMap(p => p.data) ?? []
      if (items.length > 0) {
        setSelectedPoo(items[0])
        setIsEditingName(false)
      }
    } catch {
      // silently handled
    }
  }

  const handleSelectPoo = (poo: ProofOfOwnership) => {
    setSelectedPoo(poo)
    setIsEditingName(false)
  }

  // Infinite scroll: load the next page as the operator nears the bottom.
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 80) {
      fetchNextPage()
    }
  }

  const navigateToPoo = () => {
    const url = `${getFullPath(
      PATHS.createPortfolio,
    )}?email=${encodeURIComponent(user.email)}&status=${selectedPoo.state}`
    window.open(url, "_blank")
  }

  const canUpload =
    !isFetching &&
    !pooList?.some(
      poo =>
        poo.state === PROOF_OF_OWNERSHIP_STATUS.VALIDATING ||
        poo.state === PROOF_OF_OWNERSHIP_STATUS.CREATED,
    )

  const canProcess =
    selectedPoo.id !== PooInitialState.id &&
    [
      PROOF_OF_OWNERSHIP_STATUS.CREATED,
      PROOF_OF_OWNERSHIP_STATUS.VALIDATING,
    ].includes(selectedPoo.state)

  const status = stateLabels[selectedPoo.state]
  const hasSelection = selectedPoo.id !== PooInitialState.id

  // Only blank the whole panel on the very first load — keep the list visible
  // during background refetches (e.g. after an upload).
  const isInitialLoading = isFetching && pooItems.length === 0

  if (isInitialLoading) {
    return <EmptyState>Loading...</EmptyState>
  }

  if (isErrorFetchingPooList || pooItems.length === 0) {
    return (
      <>
        <Toolbar>
          <ToolbarBtn onClick={() => setIsPooUploadModalOpened(true)}>
            Upload proof of ownership
          </ToolbarBtn>
        </Toolbar>
        {isErrorFetchingPooList ? (
          <EmptyState>
            Couldn't load proofs of ownership.{" "}
            <RetryButton onClick={handleRefresh}>Retry</RetryButton>
          </EmptyState>
        ) : (
          <EmptyState>This user has no proofs of ownership yet.</EmptyState>
        )}
        <UploadProofOfOwnershipModal
          isOpen={isPooUploadModalOpened}
          broker=""
          userId={user.id}
          userName={user.profile.name}
          onClose={() => setIsPooUploadModalOpened(false)}
          refresh={handleRefresh}
        />
      </>
    )
  }

  return (
    <>
      <Toolbar>
        <ToolbarBtn
          onClick={() => setIsPooUploadModalOpened(true)}
          disabled={!canUpload}
        >
          Upload
        </ToolbarBtn>
        <ToolbarBtn onClick={navigateToPoo} disabled={!canProcess} $primary>
          Process
        </ToolbarBtn>
      </Toolbar>

      <ContentLayout>
        <ListPanel>
          <ListHeader>
            <span>{pooItems.length} items</span>
          </ListHeader>
          <ListScroll onScroll={handleListScroll}>
            {pooItems.map(el => (
              <ProofOfOwnershipListItem
                key={el.id}
                selectedItem={selectedPoo}
                onClick={handleSelectPoo}
                item={el}
              />
            ))}
            {isFetchingNextPage && <ListFooter>Loading…</ListFooter>}
          </ListScroll>
        </ListPanel>

        <ViewerPanel>
          {hasSelection && (
            <DetailBar>
              {status && (
                <StatusChip $color={status.color}>{status.label}</StatusChip>
              )}
              <DetailItem>
                <DetailLabel>Broker</DetailLabel>
                <DetailValue>{selectedPoo.broker || "—"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                {isEditingName ? (
                  <EditNameRow>
                    <NameInput
                      value={editNameValue}
                      onChange={e => setEditNameValue(e.target.value)}
                      autoFocus
                      disabled={isSavingName}
                      onKeyDown={e => {
                        if (e.key === "Enter" && editNameValue.trim()) {
                          patchName(editNameValue.trim())
                        }
                        if (e.key === "Escape") {
                          setIsEditingName(false)
                        }
                      }}
                    />
                    <EditActionBtn
                      onClick={() => {
                        if (editNameValue.trim())
                          patchName(editNameValue.trim())
                      }}
                      disabled={!editNameValue.trim() || isSavingName}
                      $primary
                    >
                      {isSavingName ? "…" : "Save"}
                    </EditActionBtn>
                    <EditActionBtn
                      onClick={() => setIsEditingName(false)}
                      disabled={isSavingName}
                    >
                      Cancel
                    </EditActionBtn>
                  </EditNameRow>
                ) : (
                  <NameValueRow>
                    <DetailValue>{selectedPoo.name || "—"}</DetailValue>
                    {selectedPoo.state ===
                      PROOF_OF_OWNERSHIP_STATUS.VALIDATED && (
                      <EditBtn
                        onClick={() => {
                          setEditNameValue(selectedPoo.name || "")
                          setIsEditingName(true)
                        }}
                      >
                        Edit
                      </EditBtn>
                    )}
                  </NameValueRow>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Uploaded</DetailLabel>
                <DetailValue>
                  {selectedPoo.created_at
                    ? moment(selectedPoo.created_at).format("MMM D, YYYY")
                    : "—"}
                </DetailValue>
              </DetailItem>
              {selectedPoo.date && (
                <DetailItem>
                  <DetailLabel>Valid since</DetailLabel>
                  <DetailValue>
                    {moment(selectedPoo.date, "YYYY-MM-DD HH:mm:ss").format(
                      "MMM D, YYYY",
                    )}
                  </DetailValue>
                </DetailItem>
              )}
              {selectedPoo.updated_at &&
                selectedPoo.state !== PROOF_OF_OWNERSHIP_STATUS.CREATED && (
                  <DetailItem>
                    <DetailLabel>Processed</DetailLabel>
                    <DetailValue>
                      {moment(
                        selectedPoo.updated_at,
                        "YYYY-MM-DD HH:mm:ss",
                      ).format("MMM D, YYYY")}
                    </DetailValue>
                  </DetailItem>
                )}
              {selectedPoo.state === PROOF_OF_OWNERSHIP_STATUS.INVALIDATED &&
                selectedPoo.invalidation_reason && (
                  <DetailItem>
                    <DetailLabel>Reason</DetailLabel>
                    <DetailValue>{selectedPoo.invalidation_reason}</DetailValue>
                  </DetailItem>
                )}
            </DetailBar>
          )}
          <ViewerWrapper $hasDetailBar={hasSelection}>
            <ProofOfOwnershipViewer
              isLoadingList={isFetching}
              item={selectedPoo}
            />
          </ViewerWrapper>
        </ViewerPanel>
      </ContentLayout>

      <UploadProofOfOwnershipModal
        isOpen={isPooUploadModalOpened}
        broker={pooItems.length ? pooItems[0].broker : ""}
        userId={user.id}
        userName={pooItems.length ? pooItems[0].broker : user.profile.name}
        onClose={() => setIsPooUploadModalOpened(false)}
        refresh={handleRefresh}
      />
    </>
  )
}

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
`

const ToolbarBtn = styled.button<{ $primary?: boolean }>`
  height: 34px;
  padding: 0 14px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid ${p => (p.$primary ? colors.blue1 : colors.gray2)};
  background: ${p => (p.$primary ? colors.blue1 : rgba(colors.blue2, 0.08))};
  color: ${p => (p.$primary ? colors.white : colors.blue1)};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${p => (p.$primary ? colors.blue4 : rgba(colors.blue2, 0.15))};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 20px;
  color: ${colors.gray6};
  font-size: 14px;
`

const RetryButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.blue1};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const ContentLayout = styled.div`
  display: flex;
  gap: 12px;
  height: 520px;
`

const ListPanel = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.gray2};
  border-radius: 10px;
  background: ${colors.white};
  overflow: hidden;
`

const ListHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${colors.gray2};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.gray6};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const ListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px;
`

const ListFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  color: ${colors.gray6};
  font-size: 12px;
`

const ViewerPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`

const DetailBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  background: ${colors.white};
  border: 1px solid ${colors.gray2};
  border-radius: 10px 10px 0 0;
  border-bottom: none;
  flex-shrink: 0;
  flex-wrap: wrap;
`

const StatusChip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.white};
  background: ${p => p.$color};
  white-space: nowrap;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const DetailLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${colors.gray6};
`

const DetailValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.gray3};
`

const NameValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const EditBtn = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.blue1};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const EditNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const NameInput = styled.input`
  height: 24px;
  width: 140px;
  padding: 0 6px;
  font-family: inherit;
  font-size: 12px;
  border: 1px solid ${colors.gray2};
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: ${colors.blue1};
  }

  &:disabled {
    opacity: 0.6;
  }
`

const EditActionBtn = styled.button<{ $primary?: boolean }>`
  height: 24px;
  padding: 0 8px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid ${p => (p.$primary ? colors.blue1 : colors.gray2)};
  background: ${p => (p.$primary ? colors.blue1 : colors.white)};
  color: ${p => (p.$primary ? colors.white : colors.gray3)};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const ViewerWrapper = styled.div<{ $hasDetailBar?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${p => (p.$hasDetailBar ? "0 0 10px 10px" : "10px")};
`

export default ProofOfOwnershipHistorical
